import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Assessment as ReportIcon,
  ArrowBack as BackIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  ArrowDropDown as DropdownIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import ownershipReportService from '../../../../services/ownershipReportService';
import MovementTypeBadge from '../../../../components/venue/MovementTypeBadge';
import { downloadConsignmentReportExcel } from '../../../../utils/consignmentReportExcel';
import { downloadConsignmentReportPdf } from '../../../../utils/consignmentReportPdf';
import { downloadConsignmentSupplierExcel } from '../../../../utils/consignmentSupplierExcel';
import { downloadConsignmentSupplierPdf } from '../../../../utils/consignmentSupplierPdf';
import type {
  StockByOwnerEntry,
  StockByConsignorEntry,
  MovementsByOwnerRow,
  ConsignmentSummaryEntry,
  ConsignmentStatement,
  ConsignmentReconciliationResponse,
  ExportFormat,
} from '../../../../types/venue';
import { relativeDate } from '../../../../utils/dateUtils';

const colStyle = {
  fontWeight: 600,
  fontSize: '0.72rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
};

// ── Stock by Owner tab ────────────────────────────────────────────────────────

const StockByOwnerTab: React.FC<{ venueId: string }> = ({ venueId }) => {
  const theme = useTheme();
  const [data, setData] = useState<StockByOwnerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    ownershipReportService.getStockByOwner(venueId)
      .then(setData)
      .catch((err: any) => setError(err?.response?.data?.message ?? 'Failed to load stock by owner.'))
      .finally(() => setLoading(false));
  }, [venueId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {data.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No data</Typography>
      )}
      {data.map(entry => (
        <Paper key={entry.ownershipAccountId ?? '__unowned__'} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography fontWeight={700}>{entry.accountName}</Typography>
            <Chip label={entry.accountType.replace(/_/g, ' ')} size="small" variant="outlined" />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {entry.totalQuantity} units total
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Item', 'Quantity'].map(h => (
                    <TableCell key={h} sx={{ ...colStyle, color: theme.palette.text.secondary }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {entry.items.map(item => (
                  <TableRow key={item.inventoryItemId} hover>
                    <TableCell>{item.displayName}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}
    </Box>
  );
};

// ── Stock by Consignor tab ────────────────────────────────────────────────────

const StockByConsignorTab: React.FC<{ venueId: string }> = ({ venueId }) => {
  const theme = useTheme();
  const [data, setData] = useState<StockByConsignorEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    ownershipReportService.getStockByConsignor(venueId)
      .then(setData)
      .catch((err: any) => setError(err?.response?.data?.message ?? 'Failed to load stock by consignor.'))
      .finally(() => setLoading(false));
  }, [venueId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {data.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No consigned stock</Typography>
      )}
      {data.map(entry => (
        <Paper key={entry.supplierId} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography fontWeight={700}>{entry.supplierName}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {entry.totalQuantity} units total
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Account', 'Quantity'].map(h => (
                    <TableCell key={h} sx={{ ...colStyle, color: theme.palette.text.secondary }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {entry.accounts.map(acc => (
                  <TableRow key={acc.ownershipAccountId} hover>
                    <TableCell>{acc.accountName}</TableCell>
                    <TableCell>{acc.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}
    </Box>
  );
};

// ── Movements by Owner tab ────────────────────────────────────────────────────

const MovementsByOwnerTab: React.FC<{ venueId: string }> = ({ venueId }) => {
  const theme = useTheme();
  const [rows, setRows] = useState<MovementsByOwnerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ownershipReportService.getMovementsByOwner(venueId, {
        from: from || undefined,
        to: to || undefined,
        limit: 200,
      });
      setRows(result.movements);
      setTotal(result.total);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to load movements.');
    } finally {
      setLoading(false);
    }
  }, [venueId, from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="From"
          type="date"
          size="small"
          value={from}
          onChange={e => setFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 160 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={to}
          onChange={e => setTo(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 160 }}
        />
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="caption" color="text.secondary">{total} movements</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Item', 'Owner', 'Type', 'Qty', 'Location', 'Date'].map(h => (
                    <TableCell key={h} sx={{ ...colStyle, color: theme.palette.text.secondary }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>No movements</TableCell>
                  </TableRow>
                ) : (
                  rows.map(row => (
                    <TableRow key={row.movementId} hover>
                      <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.displayName}
                      </TableCell>
                      <TableCell>{row.accountName}</TableCell>
                      <TableCell><MovementTypeBadge type={row.type as any} /></TableCell>
                      <TableCell sx={{ color: row.quantity > 0 ? '#1a7f37' : '#cf222e', fontWeight: 600 }}>
                        {row.quantity > 0 ? `+${row.quantity}` : row.quantity}
                      </TableCell>
                      <TableCell>{row.locationName}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{relativeDate(row.postedAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

// ── Consignment Summary tab ───────────────────────────────────────────────────

const ConsignmentSummaryTab: React.FC<{ venueId: string; venueName: string }> = ({ venueId, venueName }) => {
  const theme = useTheme();

  // Summary data
  const [accounts, setAccounts] = useState<ConsignmentSummaryEntry[]>([]);
  const [dataLimitation, setDataLimitation] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Export state
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  // Post-export save state
  const [lastExportedReport, setLastExportedReport] = useState<ConsignmentReconciliationResponse | null>(null);
  const [lastExportFormat, setLastExportFormat] = useState<ExportFormat | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveDone, setSaveDone] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Statement history
  const [statements, setStatements] = useState<ConsignmentStatement[]>([]);

  const exportPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    ownershipReportService.getConsignmentSummary(venueId, { includeMovementSummary: true })
      .then(res => {
        setAccounts(res.accounts);
        setDataLimitation(res.dataLimitation);
      })
      .catch((err: any) => setError(err?.response?.data?.message ?? 'Failed to load consignment summary.'))
      .finally(() => setLoading(false));
  }, [venueId]);

  // Load statement history when an account is selected
  useEffect(() => {
    if (!selectedAccountId) { setStatements([]); return; }
    ownershipReportService.getStatements(venueId, { ownershipAccountId: selectedAccountId })
      .then(setStatements)
      .catch(() => setStatements([]));
  }, [venueId, selectedAccountId]);

  const handleSelectFromRow = (id: string) => {
    handleAccountChange(id);
    setTimeout(() => exportPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  };

  // Changing account clears the last export (so "Save" doesn't appear for a stale report)
  const handleAccountChange = (id: string) => {
    setSelectedAccountId(id);
    setLastExportedReport(null);
    setLastExportFormat(null);
    setSaveDone(false);
    setSaveError(null);
    setExportError(null);
  };

  const handleExport = async (action: 'supplier-excel' | 'supplier-pdf' | 'recon-excel' | 'recon-pdf') => {
    setExportAnchor(null);
    setExportError(null);
    setLastExportedReport(null);
    setSaveDone(false);
    setExportLoading(true);
    try {
      const report = await ownershipReportService.getConsignmentReconciliation(venueId, {
        ownershipAccountId: selectedAccountId,
        from:  exportFrom || undefined,
        to:    exportTo   || undefined,
        includeMovements: true,
      });
      switch (action) {
        case 'supplier-excel': downloadConsignmentSupplierExcel(report);               break;
        case 'supplier-pdf':   await downloadConsignmentSupplierPdf(report, venueName); break;
        case 'recon-excel':    downloadConsignmentReportExcel(report);                 break;
        case 'recon-pdf':      await downloadConsignmentReportPdf(report, venueName);  break;
      }
      setLastExportedReport(report);
      setLastExportFormat(action.endsWith('excel') ? 'XLSX' : 'PDF');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message
        ?? err?.response?.data?.message
        ?? 'Export failed. Please try again.';
      setExportError(msg);
    } finally {
      setExportLoading(false);
    }
  };

  const handleSave = async () => {
    if (!lastExportedReport || !lastExportFormat) return;
    setSaveLoading(true);
    setSaveError(null);
    try {
      const stmt = await ownershipReportService.createStatement(venueId, {
        ownershipAccountId:   lastExportedReport.header.ownershipAccount._id,
        supplierId:           lastExportedReport.header.supplier?._id ?? null,
        periodFrom:           lastExportedReport.header.periodFrom,
        periodTo:             lastExportedReport.header.periodTo,
        generatedAt:          lastExportedReport.header.generatedAt,
        openingTotal:         lastExportedReport.openingStock.totalQuantity,
        stockIn:              lastExportedReport.movementsInPeriod.totalIn,
        stockOut:             lastExportedReport.movementsInPeriod.totalOut,
        closingTotal:         lastExportedReport.closingStock.totalQuantity,
        reconciled:           lastExportedReport.reconciliationCheck.reconciled,
        dataLimitation:       lastExportedReport.header.dataLimitation,
        exportFormatLastUsed: lastExportFormat,
      });
      setSaveDone(true);
      setStatements(prev => [stmt, ...prev]);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message
        ?? err?.response?.data?.message
        ?? 'Failed to save statement record.';
      setSaveError(msg);
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* ── Data limitation notice ─────────────────────────────────── */}
      {dataLimitation && (
        <Alert severity="info" sx={{ fontSize: '0.8125rem' }}>
          POS sales are not yet attributed to consignment accounts. Movement summaries reflect
          goods receipt and manual movements only. This is a reconciliation report, not a payable
          settlement.
        </Alert>
      )}

      {/* ── Consignment summary table ─────────────────────────────── */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Account', 'Supplier', 'Lots Held', 'Qty', 'Total In', 'Total Out'].map(h => (
                  <TableCell key={h} sx={{ ...colStyle, color: theme.palette.text.secondary }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    No consignment accounts
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map(acc => (
                  <TableRow
                    key={acc.ownershipAccountId}
                    hover
                    selected={selectedAccountId === acc.ownershipAccountId}
                    onClick={() => handleSelectFromRow(acc.ownershipAccountId)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Tooltip title="Use for export" placement="right" arrow>
                        <span style={{
                          color: selectedAccountId === acc.ownershipAccountId ? 'inherit' : undefined,
                          textDecoration: 'underline',
                          textDecorationStyle: 'dotted',
                          textUnderlineOffset: 3,
                        }}>
                          {acc.accountName}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{acc.supplierName ?? '—'}</TableCell>
                    <TableCell>{acc.lotsHeld}</TableCell>
                    <TableCell>{acc.totalQuantity}</TableCell>
                    <TableCell sx={{ color: '#1a7f37' }}>
                      {acc.movementSummary ? `+${acc.movementSummary.totalIn}` : '—'}
                    </TableCell>
                    <TableCell sx={{ color: '#cf222e' }}>
                      {acc.movementSummary ? acc.movementSummary.totalOut : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Export panel ──────────────────────────────────────────── */}
      {accounts.length > 0 && (
        <Paper ref={exportPanelRef} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', p: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Export Reconciliation Report
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Select a consignment account to export a detailed stock reconciliation including
            opening stock, all movements (goods receipts, waste, transfers, and manual
            adjustments), and closing stock. Date range is optional. Reports can be exported
            even if there were no sales.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* Account selector */}
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Consignment Account</InputLabel>
              <Select
                label="Consignment Account"
                value={selectedAccountId}
                onChange={e => handleAccountChange(e.target.value)}
              >
                {accounts.map(acc => (
                  <MenuItem key={acc.ownershipAccountId} value={acc.ownershipAccountId}>
                    {acc.accountName}
                    {acc.supplierName ? ` (${acc.supplierName})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date range */}
            <TextField
              label="From"
              type="date"
              size="small"
              value={exportFrom}
              onChange={e => setExportFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: 155 }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={exportTo}
              onChange={e => setExportTo(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: 155 }}
            />

            {/* Export button + dropdown */}
            <Button
              variant="outlined"
              size="small"
              disabled={!selectedAccountId || exportLoading}
              onClick={e => setExportAnchor(e.currentTarget)}
              endIcon={exportLoading
                ? <CircularProgress size={14} color="inherit" />
                : <DropdownIcon />}
              sx={{ textTransform: 'none', minWidth: 130 }}
            >
              {exportLoading ? 'Generating…' : 'Export'}
            </Button>
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
            >
              <ListSubheader sx={{ lineHeight: '28px', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                Supplier Statement
              </ListSubheader>
              <MenuItem onClick={() => handleExport('supplier-excel')} sx={{ gap: 1.5 }}>
                <ExcelIcon fontSize="small" sx={{ color: '#1d6f42' }} />
                Excel
              </MenuItem>
              <MenuItem onClick={() => handleExport('supplier-pdf')} sx={{ gap: 1.5 }}>
                <PdfIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                PDF
              </MenuItem>
              <Divider />
              <ListSubheader sx={{ lineHeight: '28px', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                Internal Reconciliation
              </ListSubheader>
              <MenuItem onClick={() => handleExport('recon-excel')} sx={{ gap: 1.5 }}>
                <ExcelIcon fontSize="small" sx={{ color: '#1d6f42' }} />
                Excel
              </MenuItem>
              <MenuItem onClick={() => handleExport('recon-pdf')} sx={{ gap: 1.5 }}>
                <PdfIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                PDF
              </MenuItem>
            </Menu>
          </Box>

          {exportError && (
            <Alert severity="error" sx={{ mt: 2, fontSize: '0.8125rem' }}>{exportError}</Alert>
          )}

          {/* ── Post-export: Save Statement Record ─────────────────── */}
          {lastExportedReport && !saveDone && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="text"
                size="small"
                disabled={saveLoading}
                onClick={handleSave}
                startIcon={saveLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8125rem' }}
              >
                {saveLoading ? 'Saving…' : 'Save statement record'}
              </Button>
              <Typography variant="caption" color="text.disabled">
                Saves a log entry. Not a settlement document.
              </Typography>
            </Box>
          )}
          {saveDone && (
            <Box sx={{ mt: 2 }}>
              <Chip
                label="Statement record saved"
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            </Box>
          )}
          {saveError && (
            <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.8125rem' }}>{saveError}</Alert>
          )}
        </Paper>
      )}

      {/* ── Statement history ─────────────────────────────────────── */}
      {selectedAccountId && statements.length > 0 && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="caption" sx={{ ...colStyle, color: theme.palette.text.secondary }}>
              Statement History
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Period', 'Generated', 'Format', 'Reconciled'].map(h => (
                    <TableCell key={h} sx={{ ...colStyle, color: theme.palette.text.secondary }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {statements.map(s => (
                  <TableRow key={s._id} hover>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>
                      {new Date(s.periodFrom).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {' → '}
                      {new Date(s.periodTo).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                        {relativeDate(s.generatedAt)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {s.generatedBy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.exportFormatLastUsed}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.7rem',
                          color: s.exportFormatLastUsed === 'PDF' ? '#d32f2f' : '#1d6f42',
                          borderColor: s.exportFormatLastUsed === 'PDF' ? '#d32f2f' : '#1d6f42',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.reconciled ? 'Reconciled' : 'Variance'}
                        size="small"
                        color={s.reconciled ? 'success' : 'warning'}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const OwnershipReports: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, venueName } = useVenue();
  const [tab, setTab] = useState(0);

  if (!venueId) return null;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReportIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>Ownership Reports</Typography>
        </Box>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/admin/venues/${venueId}/inventory`)}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Back to Inventory
        </Button>
      </Box>

      <Paper sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 1 }}
        >
          <Tab label="Stock by Owner" sx={{ textTransform: 'none', fontWeight: 500 }} />
          <Tab label="Stock by Consignor" sx={{ textTransform: 'none', fontWeight: 500 }} />
          <Tab label="Movements by Owner" sx={{ textTransform: 'none', fontWeight: 500 }} />
          <Tab label="Consignment Summary" sx={{ textTransform: 'none', fontWeight: 500 }} />
        </Tabs>
        <Box sx={{ p: 2.5 }}>
          {tab === 0 && <StockByOwnerTab venueId={venueId} />}
          {tab === 1 && <StockByConsignorTab venueId={venueId} />}
          {tab === 2 && <MovementsByOwnerTab venueId={venueId} />}
          {tab === 3 && <ConsignmentSummaryTab venueId={venueId} venueName={venueName} />}
        </Box>
      </Paper>
    </Box>
  );
};

export default OwnershipReports;
